import "./adventure-event.scss";
import React, { useMemo } from "react";
import { CharacterInitializer, CharacterCollections } from "../../../../entities/characters/Character";
import { PlayerAccount } from "../../../../entities/user/PlayerAccount";
import { Adventure } from "../../../../entities/adventures/Adventure";
import { AdventureEvent, CharacterAddedAdventureEvent, CharacterRemovedAdventureEvent, CommunalDiceRolledAdventureEvent, CommunalDiceSetAdventureEvent, PlayerJoinedAdventureEvent, PlayerLeftAdventureEvent } from "../../../../entities/adventures/AdventureEvent";
import { useAppSelector } from "../../../../store/configureStore";
import { Die } from "../../../figures/Die";
import { ApplicationState } from "../../../../store/stores/ApplicationState";

type Props = {
    adventure: Adventure; //TODO: Think of any way to avoid having to pass this in so as not to call any hooks
    event: AdventureEvent;
    players: PlayerAccount[];
    characters: CharacterInitializer[];
    entities: CharacterCollections;
    includeTimestamp?: boolean;
}

export const AdventureEventComponent = (props: Props) => {
    const { adventure, event, includeTimestamp } = props;

    const date = new Date(event.occurred);
    const timestamp = `${date.toLocaleTimeString()} ${date.toLocaleDateString("en-US", { hour12: false })}`;
    return (
        <div className="adventure-event">
            <header>{adventure.name}</header>
            <div className="event-main-row">
                <div className="event-text">
                    {getEventText(event)}
                </div>
                {includeTimestamp && <div className="timestamp">{timestamp}</div>}
            </div>
        </div>
    )
}

const getEventText = (adventureEvent: AdventureEvent) => {
    switch (adventureEvent.type) {
        case "CommunalDiceRolledAdventureEvent":
            return communalDiceRolledText(adventureEvent);
        case "CommunalDiceSetAdventureEvent":
            return communalDiceSetText(adventureEvent);
        case "PlayerJoinedAdventureEvent":
            return playerJoinedText(adventureEvent);
        case "PlayerLeftAdventureEvent":
            return playerLeftText(adventureEvent);
        case "CharacterAddedAdventureEvent":
            return characterAddedText(adventureEvent);
        case "CharacterRemovedAdventureEvent":
            return characterRemovedText(adventureEvent);
    }
}

const playerJoinedText = (playerJoined: PlayerJoinedAdventureEvent) => {
    const { adventureId, playerId  } = playerJoined;
    const { adventures, players } = useAppSelector(getAdventuresPlayersCharacters);

    const adventure = useMemo(() => adventures.find(a => a.id == adventureId), [adventureId, adventures]);
    const player = useMemo(() => players.find(p => p?.id == playerId), [playerId, players]);

    return <>{getPlayerName(player)} joined {getAdventureName(adventure)}.</>;
}

const playerLeftText = (playerLeft: PlayerLeftAdventureEvent) => {
    const { adventureId, playerId } = playerLeft;
    const { adventures, players } = useAppSelector(getAdventuresPlayersCharacters);

    const adventure = useMemo(() => adventures.find(a => a.id == adventureId), [adventureId, adventures]);
    const player = useMemo(() => players.find(p => p?.id == playerId), [playerId, players]);

    return <>{getPlayerName(player)} left {getAdventureName(adventure)}</>
}

const characterAddedText = (characterAdded: CharacterAddedAdventureEvent) => {
    const { adventureId, playerId, characterId } = characterAdded;
    const { adventures, players, characters } = useAppSelector(getAdventuresPlayersCharacters);

    const adventure = useMemo(() => adventures.find(a => a.id == adventureId), [adventureId, adventures]);
    const player = useMemo(() => players.find(p => p?.id == playerId), [playerId, players]);
    const character = useMemo(() => characters.find(c => c.id == characterId), [characterId, characters]);

    return <>
        {getCharacterName(character)} ({getPlayerName(player)}) was added to {getAdventureName(adventure)}.
    </>;
}

const characterRemovedText = (characterRemoved: CharacterRemovedAdventureEvent) => {
    const { playerId, characterId } = characterRemoved;
    const { adventures, players, characters } = useAppSelector(getAdventuresPlayersCharacters);
    
    const player = useMemo(() => players.find(p => p?.id == playerId), [playerId, players]);
    const character = useMemo(() => characters.find(c => c.id == characterId), [characterId, characters]);

    return <>{getCharacterName(character)} ({getPlayerName(player)}) was removed from the adventure.</>;
}

const communalDiceSetText = (communalDiceSet: CommunalDiceSetAdventureEvent) => {
    const { adventureId, communalDice } = communalDiceSet;
    const { adventures, players } = useAppSelector(getAdventuresPlayersCharacters);

    const adventure = useMemo(() => adventures.find(a => a.id == adventureId), [adventureId, adventures]);
    const player = useMemo(() => players.find(p => p?.id == adventure?.ownerId), [adventure, players]);

    return <>{getPlayerName(player)} set the communal dice to {communalDice.map(dieFace => <Die>{dieFace}</Die>)}</>;
}

const communalDiceRolledText = (communalDiceRolled: CommunalDiceRolledAdventureEvent) => {
    const { adventureId, communalDice } = communalDiceRolled;
    const { adventures, players } = useAppSelector(getAdventuresPlayersCharacters);

    const adventure = useMemo(() => adventures.find(a => a.id == adventureId), [adventureId, adventures]);
    const player = useMemo(() => players.find(p => p?.id == adventure?.ownerId), [adventure, players]);

    return <>{getPlayerName(player)} rolled the communal dice and got <span className="no-text-break">{communalDice.map((dieFace, index) => <Die key={index}>{dieFace}</Die>)}</span></>;
}


const getAdventuresPlayersCharacters = (app: ApplicationState) => ({
    adventures: app.adventure.adventures,
    players: [app.player.me, ...app.player.players],
    characters: [...app.character.characters, ...app.character.otherPlayerCharacters]
})

const getAdventureName = (adventure: Adventure | null | undefined) => {
    if (adventure != null) {
        return <em>{adventure.name}</em>;
    }
    return 'an unknown adventure';
}

const getPlayerName = (player: PlayerAccount | null | undefined) => player?.displayName ?? "an unknown player";
const getCharacterName = (character: CharacterInitializer | null | undefined) => character?.name ?? "an unknown character";